// HelpTooltip.tsx — v11.8.8.1
// The 3-part "?" help tooltip from the AI Agent prototype (What it does / When it fires / Example).
// v11.8.8.1 (AAR15): the tip is now position:fixed and JS-positioned so it escapes the section's
// overflow:hidden and clamps to the viewport (never clipped off the left/top edge), with the arrow
// pointing at the icon — a direct port of the prototype's placeFixedTooltip/TipMgr behavior.
import { Fragment, ReactNode, useCallback, useEffect, useRef } from 'react';

export type HelpSection = [label: string, body: ReactNode];

export function HelpTooltip({ sections, label = 'Help' }: { sections: HelpSection[]; label?: string }) {
  const wrapRef = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLSpanElement>(null);
  const hideTimer = useRef<number | undefined>(undefined);

  const place = useCallback(() => {
    const trig = wrapRef.current;
    const tip = tipRef.current;
    if (!trig || !tip) return;
    const margin = 8;
    const gap = 10;
    tip.classList.add('measuring');
    tip.classList.remove('tip-above', 'tip-below');
    const t = trig.getBoundingClientRect();
    const r = tip.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = t.left + t.width / 2 - r.width / 2;
    left = Math.max(margin, Math.min(left, vw - r.width - margin));
    const above = t.top - gap - r.height >= margin;
    let top = above ? t.top - gap - r.height : t.bottom + gap;
    if (top + r.height > vh - margin) top = Math.max(margin, vh - r.height - margin);
    tip.style.left = Math.round(left) + 'px';
    tip.style.top = Math.round(top) + 'px';
    tip.classList.add(above ? 'tip-above' : 'tip-below');
    const cx = t.left + t.width / 2;
    const arrowX = Math.max(14, Math.min(r.width - 14, cx - left));
    tip.style.setProperty('--arrow-x', Math.round(arrowX) + 'px');
    tip.classList.remove('measuring');
  }, []);

  const clearTimer = () => {
    if (hideTimer.current) { window.clearTimeout(hideTimer.current); hideTimer.current = undefined; }
  };
  const show = useCallback(() => { clearTimer(); place(); tipRef.current?.classList.add('show'); }, [place]);
  const hide = useCallback(() => { clearTimer(); tipRef.current?.classList.remove('show'); }, []);
  const scheduleHide = useCallback(() => {
    clearTimer();
    hideTimer.current = window.setTimeout(() => tipRef.current?.classList.remove('show'), 120);
  }, []);

  useEffect(() => {
    const onScroll = () => { if (tipRef.current?.classList.contains('show')) place(); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') hide(); };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
      document.removeEventListener('keydown', onKey);
    };
  }, [place, hide]);

  return (
    <span
      className="help"
      ref={wrapRef}
      tabIndex={0}
      role="button"
      aria-label={label}
      onMouseEnter={show}
      onMouseLeave={scheduleHide}
      onFocus={show}
      onBlur={scheduleHide}
    >
      ?
      <span
        className="tip"
        role="tooltip"
        ref={tipRef}
        onMouseEnter={clearTimer}
        onMouseLeave={scheduleHide}
      >
        <span className="tip-body">
          {sections.map(([title, body], i) => (
            <Fragment key={i}>
              <strong>{title}</strong>
              <p>{body}</p>
            </Fragment>
          ))}
        </span>
      </span>
    </span>
  );
}

export default HelpTooltip;
